import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Accessibility, Baby, Maximize2, Stethoscope, Shield, ArrowLeft, ChevronRight, CheckCircle, Phone } from "lucide-react";
import { KioskViewport, useKioskSpeech } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";

const IDLE_TIMEOUT = 60;
const CATEGORY_MESSAGE = "Select your parking category. Choose the category that best describes your designated parking needs.";

const categories = [
  { id: "disabled_blue_badge", name: "Blue Badge", description: "For drivers with a valid Blue Badge", icon: Accessibility, color: "#3B82F6", bgColor: "#EFF6FF", requiresOCR: true },
  { id: "adult_with_child", name: "Adult with Child", description: "For parents with children under 12", icon: Baby, color: "#EC4899", bgColor: "#FDF2F8", requiresOCR: false },
  { id: "nhs_staff", name: "NHS Staff", description: "For NHS employees with valid ID", icon: Stethoscope, color: "#10B981", bgColor: "#ECFDF5", requiresOCR: false },
  { id: "military", name: "Military", description: "For active or former service members", icon: Shield, color: "#6366F1", bgColor: "#EEF2FF", requiresOCR: false },
  { id: "extra_wide", name: "Extra Wide Bay", description: "For larger vehicles requiring extra space", icon: Maximize2, color: "#F59E0B", bgColor: "#FFFBEB", requiresOCR: false }
];

export default function KioskCategory() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const idleTimerRef = useRef(null);
  
  useKioskSpeech('KioskCategory', CATEGORY_MESSAGE, true);

  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  const handleContinue = () => {
    if (selectedCategory) {
      const category = categories.find(c => c.id === selectedCategory);
      const { icon, ...serializableCategory } = category;
      navigate("/kiosk/form", { state: { category: serializableCategory } });
    }
  };

  return (
    <KioskViewport>
      <div 
        style={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          background: '#0d2137'
        }} 
        data-testid="kiosk-category"
      >
        {/* STANDARDIZED HEADER with approved logo */}
        <KioskHeader showDateTime={true} showProgress={true} currentStep={1} />

        {/* MAIN CONTENT - FULL WIDTH */}
        <main style={{ 
          flex: 1, 
          padding: '24px 32px',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)',
          overflow: 'auto'
        }}>
          {/* BACK BUTTON - TOP OF CONTENT AREA */}
          <button 
            onClick={() => navigate("/kiosk")} 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 24px',
              borderRadius: '14px',
              backgroundColor: '#1e3a5f',
              border: 'none',
              cursor: 'pointer',
              alignSelf: 'flex-start',
              marginBottom: '20px'
            }}
            data-testid="back-button"
          >
            <ArrowLeft style={{ width: '28px', height: '28px', color: 'white' }} />
            <span style={{ color: 'white', fontWeight: '700', fontSize: '20px', fontFamily: 'Outfit, sans-serif' }}>Back</span>
          </button>
          
          {/* TRACY + SPEECH BUBBLE - FULL WIDTH */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '20px',
            marginBottom: '20px'
          }}>
            <img 
              src="/tracy.png" 
              alt="ParkWise Assistant" 
              style={{ height: '180px', objectFit: 'contain', flexShrink: 0 }} 
            />
            <div style={{ 
              flex: 1,
              backgroundColor: '#fef3c7',
              border: '3px solid #fde68a',
              borderRadius: '24px',
              padding: '20px 28px',
              boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                left: '-16px',
                top: '35px',
                width: '0',
                height: '0',
                borderTop: '14px solid transparent',
                borderBottom: '14px solid transparent',
                borderRight: '18px solid #fef3c7'
              }}></div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#1e293b',
                marginBottom: '8px',
                fontFamily: 'Outfit, sans-serif'
              }}>
                Select your parking category
              </h2>
              <p style={{
                fontSize: '20px',
                color: '#475569',
                lineHeight: '1.4',
                margin: 0
              }}>
                Choose the category that best describes your designated parking needs.
              </p>
            </div>
          </div>

          {/* CATEGORY CARDS - FULL WIDTH */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            marginBottom: '20px'
          }}>
            {categories.map((category) => {
              const Icon = category.icon;
              const isSelected = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: '16px 24px',
                    border: isSelected ? '4px solid #22c55e' : '4px solid transparent',
                    boxShadow: isSelected ? '0 6px 24px rgba(34,197,94,0.3)' : '0 3px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    textAlign: 'left',
                    minHeight: '90px',
                    width: '100%'
                  }}
                  data-testid={`category-${category.id}`}
                >
                  <div style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    backgroundColor: category.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Icon style={{ width: '38px', height: '38px', color: category.color }} />
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '26px',
                      fontWeight: '700',
                      color: '#1e293b',
                      marginBottom: '2px',
                      fontFamily: 'Outfit, sans-serif'
                    }}>{category.name}</h3>
                    <p style={{
                      fontSize: '17px',
                      color: '#64748b',
                      margin: 0
                    }}>{category.description}</p>
                  </div>
                  
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    backgroundColor: isSelected ? '#22c55e' : '#e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <CheckCircle style={{ 
                      width: '26px', 
                      height: '26px', 
                      color: isSelected ? 'white' : '#94a3b8' 
                    }} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* CONTINUE BUTTON + HELP - DIRECTLY AFTER CATEGORIES */}
          <div style={{
            padding: '20px 24px',
            backgroundColor: '#0d2137',
            borderRadius: '16px'
          }}>
            {/* Selection indicator */}
            {selectedCategory && (
              <div style={{
                backgroundColor: '#dcfce7',
                border: '2px solid #86efac',
                borderRadius: '12px',
                padding: '10px 20px',
                textAlign: 'center',
                marginBottom: '16px'
              }}>
                <p style={{ fontSize: '18px', color: '#166534', fontWeight: '600', margin: 0, fontFamily: 'Outfit, sans-serif' }}>
                  <strong>{categories.find(c => c.id === selectedCategory)?.name}</strong> selected
                  {categories.find(c => c.id === selectedCategory)?.requiresOCR && " — You'll scan your Blue Badge next"}
                </p>
              </div>
            )}
            
            {/* Continue button - centered */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <button
                onClick={handleContinue}
                disabled={!selectedCategory}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '18px 64px',
                  borderRadius: '14px',
                  backgroundColor: selectedCategory ? '#16a34a' : '#64748b',
                  border: 'none',
                  cursor: selectedCategory ? 'pointer' : 'not-allowed',
                  boxShadow: selectedCategory ? '0 6px 20px rgba(22,163,74,0.4)' : 'none'
                }}
                data-testid="continue-button"
              >
                <span style={{ fontSize: '24px', fontWeight: '700', color: 'white', fontFamily: 'Outfit, sans-serif' }}>CONTINUE</span>
                <ChevronRight style={{ width: '30px', height: '30px', color: 'white' }} />
              </button>
            </div>
            
            {/* Help line */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '12px',
              padding: '14px',
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '12px'
            }}>
              <Phone style={{ width: '24px', height: '24px', color: '#fbbf24' }} />
              <span style={{ color: 'white', fontWeight: '600', fontSize: '18px', fontFamily: 'Outfit, sans-serif' }}>Need help? Call </span>
              <span style={{ color: '#fbbf24', fontWeight: '700', fontSize: '22px', fontFamily: 'Outfit, sans-serif' }}>01292 833107</span>
            </div>
          </div>
        </main>
      </div>
    </KioskViewport>
  );
}
